import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import {
  BANK_LINE_KIND_LABELS_RU,
  BANK_LINE_STATUS_LABELS_RU,
  BANK_SKIP_REASON_LABELS_RU,
  BANK_MATCH_STATE_LABELS_RU,
  type BankLineDto,
} from '@crm/shared'
import { unallocateBankLine, updateBankLine } from '../../shared/api/bank.api'
import { apiErrorText } from '../../shared/api/errorText'
import { confirm } from '../../shared/ui/confirmStore'
import { toast } from '../../shared/ui/toastStore'
import { BrokerGrid } from '../customs/broker/BrokerGrid'
import { BankAllocateModal } from './BankAllocateModal'
import {
  BANK_OPEN_LINES_KEY,
  BANK_STATEMENTS_KEY,
  BankKindBadge,
  BankMatchBadge,
  BankStatusBadge,
  formatBankDate,
  formatBankMoney,
} from './bankFormat'

type LineRow = {
  id: string
  postedLabel: string
  postedAt: string
  amount: number
  remaining: number
  counterparty: string
  inn: string
  invoice: string
  purpose: string
  kindLabel: string
  statusLabel: string
  matchLabel: string
  target: string
  docNumber: string
  statementPeriod: string
  raw: BankLineDto
}

function toRow(l: BankLineDto): LineRow {
  return {
    id: l.id,
    postedLabel: formatBankDate(l.postedAt),
    postedAt: l.postedAt ?? '',
    amount: l.amount,
    remaining: l.remaining,
    counterparty: l.client?.name ?? l.match?.clientName ?? l.counterpartyName,
    inn: l.counterpartyInn,
    invoice: l.invoiceRefs
      .map((r) => (r.date ? `${r.number} от ${formatBankDate(r.date)}` : r.number))
      .join(', '),
    purpose: l.purpose,
    kindLabel: BANK_LINE_KIND_LABELS_RU[l.kind],
    statusLabel: l.skipReason
      ? `${BANK_LINE_STATUS_LABELS_RU[l.status]}: ${BANK_SKIP_REASON_LABELS_RU[l.skipReason].toLowerCase()}`
      : BANK_LINE_STATUS_LABELS_RU[l.status],
    matchLabel: l.match ? BANK_MATCH_STATE_LABELS_RU[l.match.state] : '',
    target:
      l.allocations.length > 0
        ? l.allocations.map((a) => `Заказ ${a.requestNumber ?? '—'}`).join('; ')
        : (l.match?.proposalLabel ?? ''),
    docNumber: l.docNumber,
    statementPeriod: l.statementPeriod ?? '',
    raw: l,
  }
}

const rightAligned = {
  filter: false,
  cellClass: 'text-right tabular-nums',
  headerClass: 'ag-right-aligned-header',
} as const

/**
 * Таблица строк выписки (этап 3, Б3) — общая для страницы выписки и очереди
 * «не разнесено»: что программа решила про поступление, куда оно разнесено и
 * действия бухгалтера («Разнести», «Отменить разнос», «Не учитывать»).
 */
export function BankLinesTable({
  lines,
  canEdit,
  storageKey,
  className,
  invalidateKeys,
  showStatement = false,
}: {
  lines: BankLineDto[]
  canEdit: boolean
  storageKey: string
  className: string
  /** Какие запросы перечитать после действия (выписка или очередь). */
  invalidateKeys: ReadonlyArray<ReadonlyArray<string>>
  showStatement?: boolean
}) {
  const qc = useQueryClient()
  const [allocating, setAllocating] = useState<BankLineDto | null>(null)
  const rows = useMemo(() => lines.map(toRow), [lines])

  const refresh = () => {
    for (const key of [...invalidateKeys, BANK_OPEN_LINES_KEY, BANK_STATEMENTS_KEY]) {
      void qc.invalidateQueries({ queryKey: [...key] })
    }
  }

  const setStatus = async (line: BankLineDto, status: 'PENDING' | 'SKIPPED') => {
    try {
      await updateBankLine(line.id, status)
      refresh()
      toast.success(
        status === 'SKIPPED' ? 'Строка убрана из разноса' : 'Строка возвращена в работу',
      )
    } catch (e) {
      toast.error(apiErrorText(e, 'Не удалось изменить строку'))
    }
  }

  const unallocate = async (line: BankLineDto) => {
    const ok = await confirm({
      title: 'Отменить разнос платежа?',
      message:
        'Оплаты из банка по этой строке будут удалены из заказов, ручные оплаты останутся. Статусы заказов пересчитаются, строка вернётся в очередь.',
      confirmText: 'Отменить разнос',
      danger: true,
    })
    if (!ok) return
    try {
      await unallocateBankLine(line.id)
      refresh()
      toast.success('Разнос отменён — строка снова в очереди')
    } catch (e) {
      toast.error(apiErrorText(e, 'Не удалось отменить разнос'))
    }
  }

  const colDefs = useMemo<ColDef<LineRow>[]>(
    () => [
      {
        field: 'postedLabel',
        headerName: 'Дата',
        minWidth: 110,
        filterParams: { dateMode: true },
        comparator: (_a, _b, nodeA, nodeB) =>
          (nodeA.data?.postedAt ?? '').localeCompare(nodeB.data?.postedAt ?? ''),
      },
      {
        field: 'amount',
        headerName: 'Сумма',
        minWidth: 140,
        ...rightAligned,
        cellRenderer: ({ data }: ICellRendererParams<LineRow>) =>
          data ? (
            <span className={data.raw.direction === 'OUT' ? 'text-gray-500' : 'text-gray-900'}>
              {data.raw.direction === 'OUT' ? '−' : ''}
              {formatBankMoney(data.amount)}
            </span>
          ) : null,
      },
      {
        field: 'remaining',
        headerName: 'Не разнесено',
        minWidth: 140,
        ...rightAligned,
        cellRenderer: ({ data }: ICellRendererParams<LineRow>) =>
          data && data.raw.direction === 'IN' && data.raw.status !== 'SKIPPED' ? (
            <span>{formatBankMoney(data.remaining)}</span>
          ) : (
            <span className="text-gray-400">—</span>
          ),
      },
      { field: 'counterparty', headerName: 'Клиент / контрагент', minWidth: 240 },
      { field: 'inn', headerName: 'ИНН', minWidth: 130, cellClass: 'font-mono' },
      {
        field: 'invoice',
        headerName: 'Счёт из назначения',
        minWidth: 170,
        cellRenderer: ({ data }: ICellRendererParams<LineRow>) =>
          data?.invoice ? <span>{data.invoice}</span> : <span className="text-gray-400">—</span>,
      },
      {
        field: 'matchLabel',
        headerName: 'Подбор',
        minWidth: 170,
        cellRenderer: ({ data }: ICellRendererParams<LineRow>) =>
          data?.raw.match ? (
            <span title={data.raw.match.reason}>
              <BankMatchBadge state={data.raw.match.state} />
            </span>
          ) : (
            <span className="text-gray-400">—</span>
          ),
      },
      {
        field: 'target',
        headerName: 'Заказ',
        minWidth: 200,
        cellRenderer: ({ data }: ICellRendererParams<LineRow>) => {
          if (!data) return null
          if (data.raw.allocations.length > 0) {
            return (
              <span className="flex flex-wrap gap-x-2">
                {data.raw.allocations.map((a) => (
                  <Link
                    key={a.paymentId}
                    to={`/orders/${a.orderId}`}
                    className="text-brand-600 hover:text-brand-700"
                    title={`${formatBankMoney(a.amount)}${a.source === 'MANUAL' ? ' — оплата была внесена вручную' : ''}`}
                  >
                    {a.requestNumber ?? 'заказ'}
                  </Link>
                ))}
              </span>
            )
          }
          return data.target ? (
            <span className="text-gray-500" title="Предложение программы">
              {data.target}
            </span>
          ) : (
            <span className="text-gray-400">—</span>
          )
        },
      },
      {
        field: 'purpose',
        headerName: 'Назначение платежа',
        flex: 1,
        minWidth: 300,
        cellRenderer: ({ data }: ICellRendererParams<LineRow>) =>
          data ? <span title={data.purpose}>{data.purpose}</span> : null,
      },
      {
        field: 'kindLabel',
        headerName: 'Вид',
        minWidth: 130,
        cellRenderer: ({ data }: ICellRendererParams<LineRow>) =>
          data ? <BankKindBadge kind={data.raw.kind} label={data.kindLabel} /> : null,
      },
      {
        field: 'statusLabel',
        headerName: 'Статус',
        minWidth: 170,
        cellRenderer: ({ data }: ICellRendererParams<LineRow>) =>
          data ? <BankStatusBadge status={data.raw.status} label={data.statusLabel} /> : null,
      },
      { field: 'docNumber', headerName: '№ платёжки', minWidth: 120 },
      ...(showStatement
        ? [
            {
              field: 'statementPeriod',
              headerName: 'Выписка',
              minWidth: 170,
              cellRenderer: ({ data }: ICellRendererParams<LineRow>) =>
                data ? (
                  <Link
                    to={`/bank/statements/${data.raw.statementId}`}
                    className="text-brand-600 hover:text-brand-700"
                  >
                    {data.statementPeriod || 'выписка'}
                  </Link>
                ) : null,
            } satisfies ColDef<LineRow>,
          ]
        : []),
      ...(canEdit
        ? [
            {
              colId: 'actions',
              headerName: '',
              minWidth: 230,
              width: 240,
              pinned: 'right',
              sortable: false,
              filter: false,
              cellRenderer: ({ data }: ICellRendererParams<LineRow>) => {
                if (!data || data.raw.direction !== 'IN') return null
                const l = data.raw
                const open = l.status === 'PENDING' || l.status === 'PARTIAL'
                return (
                  <span className="flex items-center gap-3">
                    {open && (
                      <button
                        type="button"
                        onClick={() => setAllocating(l)}
                        className="text-xs font-medium text-brand-600 hover:text-brand-700"
                      >
                        Разнести
                      </button>
                    )}
                    {l.allocations.length > 0 && (
                      <button
                        type="button"
                        onClick={() => void unallocate(l)}
                        className="text-xs text-gray-500 hover:text-gray-800"
                      >
                        Отменить разнос
                      </button>
                    )}
                    {l.status === 'PENDING' && (
                      <button
                        type="button"
                        onClick={() => void setStatus(l, 'SKIPPED')}
                        className="text-xs text-gray-500 hover:text-gray-800"
                      >
                        Не учитывать
                      </button>
                    )}
                    {l.status === 'SKIPPED' && (
                      <button
                        type="button"
                        onClick={() => void setStatus(l, 'PENDING')}
                        className="text-xs text-brand-600 hover:text-brand-700"
                      >
                        Вернуть в работу
                      </button>
                    )}
                  </span>
                )
              },
            } satisfies ColDef<LineRow>,
          ]
        : []),
    ],
    // Действия замыкают только стабильные qc и ключи перечитывания.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canEdit, showStatement],
  )

  return (
    <>
      <BrokerGrid<LineRow>
        rowData={rows}
        columnDefs={colDefs}
        storageKey={storageKey}
        className={className}
        noRowsText="Строк нет"
      />
      {allocating && (
        <BankAllocateModal
          line={allocating}
          onClose={() => setAllocating(null)}
          onDone={() => {
            setAllocating(null)
            refresh()
          }}
        />
      )}
    </>
  )
}
